import { useState } from "react";
import { Printer, CalendarDays, Clock, Equal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/common";
import { MonthPicker } from "@/components/DatePicker";
import { inr, toPaise, currentMonth, monthLabel } from "@/lib/format";

const daysIn = (m) => (m ? new Date(+m.slice(0, 4), +m.slice(5, 7), 0).getDate() : 0);
const num = (s) => {
  const c = String(s ?? "").replace(/,/g, "").trim();
  return c === "" || !/^\d*\.?\d*$/.test(c) ? null : Number(c);
};

function Field({ label, value, onChange, testid, hint, error, suffix }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative mt-1.5">
        <Input inputMode="decimal" className={`h-11 money ${error ? "border-red-500" : ""}`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="0" />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>}
      </div>
      {error ? <div className="text-xs text-red-700 mt-1" data-testid={`${testid}-error`}>{error}</div> : hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function ResultCard({ lines, total, formula, testid }) {
  return (
    <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-7 shadow-lg relative overflow-hidden" data-testid={testid}>
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-600/25 blur-2xl" />
      <div className="relative space-y-3">
        {lines.map(([l, v, id]) => (
          <div key={l} className="flex items-center justify-between gap-3">
            <span className="text-[11px] uppercase tracking-wider text-slate-400">{l}</span>
            <span className="money text-lg" data-testid={`${testid}-${id}`}>{v}</span>
          </div>
        ))}
        <div className="border-t border-white/15 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Equal className="h-3.5 w-3.5" /> Gross salary</div>
          <div className="money font-extrabold text-4xl sm:text-5xl mt-2 tracking-tight" data-testid={`${testid}-gross`}>{total}</div>
          <div className="text-xs text-slate-400 mt-3 money" data-testid={`${testid}-formula`}>{formula}</div>
        </div>
      </div>
    </div>
  );
}

function Layout({ form, result }) {
  return (
    <div className="grid lg:grid-cols-5 gap-5 fade-up">
      <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-4">{form}</div>
      <div className="lg:col-span-2 space-y-3">
        {result}
        <Button variant="outline" className="w-full no-print" onClick={() => window.print()} data-testid="salary-print-btn"><Printer className="h-4 w-4" /> Print</Button>
      </div>
    </div>
  );
}

function DayWise() {
  const [salary, setSalary] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [days, setDays] = useState("");
  const total = daysIn(month);
  const sp = toPaise(salary);
  const d = num(days);
  const err = days !== "" && (d === null ? "Enter a valid number of days" : d > total ? `Cannot be more than ${total} days in ${monthLabel(month)}` : "");
  const ok = sp !== null && d !== null && !err;
  const perDay = ok ? sp / total : 0;
  const gross = ok ? Math.round((sp * d) / total) : 0;
  return (
    <Layout
      form={<>
        <Field label="Monthly salary (₹)" value={salary} onChange={setSalary} testid="day-salary-input" error={salary && sp === null ? "Enter a valid amount" : ""} />
        <div><Label>Salary month</Label><div className="mt-1.5"><MonthPicker value={month} onChange={setMonth} testid="day-month-picker" /></div><div className="text-xs text-slate-500 mt-1" data-testid="day-days-in-month">{total} days in {monthLabel(month)}</div></div>
        <Field label="Days worked" value={days} onChange={setDays} testid="day-worked-input" error={err} hint="Half days allowed, e.g. 25.5" suffix="days" />
      </>}
      result={<ResultCard testid="day-result" total={inr(gross / 100)}
        lines={[["Days in month", total, "total-days"], ["Per-day rate", inr(perDay / 100), "per-day"], ["Days worked", d ?? 0, "days"]]}
        formula={`${inr((sp || 0) / 100)} ÷ ${total} × ${d ?? 0}`} />}
    />
  );
}

function HourWise() {
  const [salary, setSalary] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [shift, setShift] = useState("8");
  const [hours, setHours] = useState("");
  const total = daysIn(month);
  const sp = toPaise(salary);
  const sh = num(shift);
  const h = num(hours);
  const shiftErr = shift !== "" && (sh === null || sh <= 0 || sh > 24) ? "Shift hours must be between 0 and 24" : "";
  const hourErr = hours !== "" && h === null ? "Enter valid hours" : "";
  const ok = sp !== null && sh > 0 && !shiftErr && h !== null && !hourErr;
  const totalHours = sh > 0 && !shiftErr ? total * sh : 0;
  const perHour = ok ? sp / totalHours : 0;
  const gross = ok ? Math.round((sp * h) / totalHours) : 0;
  const over = ok && h > totalHours;
  return (
    <Layout
      form={<>
        <Field label="Monthly salary (₹)" value={salary} onChange={setSalary} testid="hour-salary-input" error={salary && sp === null ? "Enter a valid amount" : ""} />
        <div><Label>Salary month</Label><div className="mt-1.5"><MonthPicker value={month} onChange={setMonth} testid="hour-month-picker" /></div><div className="text-xs text-slate-500 mt-1" data-testid="hour-days-in-month">{total} days in {monthLabel(month)}</div></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Shift hours per day" value={shift} onChange={setShift} testid="hour-shift-input" error={shiftErr} suffix="hrs" />
          <Field label="Hours worked" value={hours} onChange={setHours} testid="hour-worked-input" error={hourErr} hint="Decimals allowed, e.g. 7.5" suffix="hrs" />
        </div>
        {over && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="hour-over-warning">Hours worked are more than the month's total shift hours ({totalHours}).</div>}
      </>}
      result={<ResultCard testid="hour-result" total={inr(gross / 100)}
        lines={[["Total shift hours", `${total} × ${sh || 0} = ${totalHours}`, "total-hours"], ["Per-hour rate", inr(perHour / 100), "per-hour"], ["Hours worked", h ?? 0, "hours"]]}
        formula={`${inr((sp || 0) / 100)} ÷ (${total} × ${sh || 0}) × ${h ?? 0}`} />}
    />
  );
}

export default function SalaryCalculator() {
  return (
    <div>
      <PageHeader title="Salary Calculator" subtitle="Quick gross salary working, day-wise or hour-wise. Nothing is saved." />
      <Tabs defaultValue="day">
        <TabsList className="mb-4 no-print">
          <TabsTrigger value="day" data-testid="salary-tab-day"><CalendarDays className="h-4 w-4 mr-1.5" /> Day-wise</TabsTrigger>
          <TabsTrigger value="hour" data-testid="salary-tab-hour"><Clock className="h-4 w-4 mr-1.5" /> Hour-wise</TabsTrigger>
        </TabsList>
        <TabsContent value="day"><DayWise /></TabsContent>
        <TabsContent value="hour"><HourWise /></TabsContent>
      </Tabs>
    </div>
  );
}
